import { ArrowRightSmallIcon, CameraIcon } from '@blocksuite/icons/rc';
import { FlexWrapper, Input, notify } from '@pique/component';
import {
  SettingHeader,
  SettingRow,
  SettingWrapper,
} from '@pique/component/setting-components';
import { Avatar } from '@pique/component/ui/avatar';
import { Button } from '@pique/component/ui/button';
import { ConfirmModal } from '@pique/component/ui/modal';
import { useAsyncCallback } from '@pique/core/hooks/pique-async-hooks';
import { useCatchEventCallback } from '@pique/core/hooks/use-catch-event-hook';
import { useI18n } from '@pique/i18n';
import {
  useLiveData,
  useService,
  useServices,
  WorkspacesService,
} from '@toeverything/infra';
import { useSetAtom } from 'jotai';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { authAtom, openSettingModalAtom, openSignOutModalAtom } from '../../../../atoms';
import { AuthService, SubscriptionService } from '../../../../modules/cloud';
import { Upload } from '../../../pure/file-upload';
import * as styles from './style.css';

export const UserAvatar = () => {
  const t = useI18n();
  const session = useService(AuthService).session;
  const account = useLiveData(session.account$);

  const handleUpdateUserAvatar = useAsyncCallback(
    async (file: File) => {
      if (!account) return;
      try {
        await session.uploadAvatar(file);
        notify.success({ title: 'Update user avatar success' });
      } catch (e) {
        notify.error({
          title: 'Update user avatar failed',
          message: String(e),
        });
      }
    },
    [session, account]
  );

  const handleRemoveUserAvatar = useCatchEventCallback(async () => {
    await session.removeAvatar();
  }, [session]);

  if (!account) return null;

  return (
    <Upload
      accept="image/gif,image/jpeg,image/jpg,image/png,image/svg"
      fileChange={handleUpdateUserAvatar}
      data-testid="upload-user-avatar"
    >
      <Avatar
        size={56}
        name={account.label}
        url={account.avatar}
        hoverIcon={<CameraIcon />}
        onRemove={account.avatar ? handleRemoveUserAvatar : undefined}
        avatarTooltipOptions={{ content: t['Click to replace photo']() }}
        removeTooltipOptions={{ content: t['Remove photo']() }}
        data-testid="user-setting-avatar"
        removeButtonProps={{
          ['data-testid' as string]: 'user-setting-remove-avatar-button',
        }}
      />
    </Upload>
  );
};

export const AvatarAndName = () => {
  const t = useI18n();
  const session = useService(AuthService).session;
  const account = useLiveData(session.account$);
  const [input, setInput] = useState<string>(account?.label ?? '');

  useEffect(() => {
    if (account?.label) {
      setInput(account.label);
    }
  }, [account?.label]);

  const allowUpdate = !!input && account && input !== account.label;
  const handleUpdateUserName = useAsyncCallback(async () => {
    if (!account || !allowUpdate) return;

    try {
      await session.updateLabel(input);
    } catch (e) {
      notify.error({
        title: 'Failed to update user name.',
        message: String(e),
      });
    }
  }, [account, allowUpdate, session, input]);

  if (!account) return null;

  return (
    <div className={styles.avatarSection}>
      <UserAvatar />
      <div className={styles.profileInputWrapper}>
        <label>{t['com.pique.settings.profile.name']()}</label>
        <FlexWrapper alignItems="center" style={{ gap: '8px' }}>
          <Input
            defaultValue={input}
            data-testid="user-name-input"
            placeholder={t['com.pique.settings.profile.placeholder']()}
            maxLength={64}
            minLength={0}
            style={{ width: 240, height: 28 }}
            onChange={setInput}
            onEnter={handleUpdateUserName}
          />
          {allowUpdate ? (
            <Button
              data-testid="save-user-name"
              onClick={handleUpdateUserName}
              style={{ height: 28, fontSize: '12px' }}
            >
              {t['com.pique.editCollection.save']()}
            </Button>
          ) : null}
        </FlexWrapper>
      </div>
    </div>
  );
};

const PlanSection: FC = () => {
  const subscriptionService = useService(SubscriptionService);
  const setOpenSettingModal = useSetAtom(openSettingModalAtom);

  useEffect(() => {
    subscriptionService.subscription.revalidate();
  }, [subscriptionService]);

  const proSubscription = useLiveData(subscriptionService.subscription.pro$);
  const isFreePlan = !proSubscription;

  const openPlans = useCallback(() => {
    setOpenSettingModal({ open: true, activeTab: 'plans' });
  }, [setOpenSettingModal]);

  const openBilling = useCallback(() => {
    setOpenSettingModal({ open: true, activeTab: 'plans' });
  }, [setOpenSettingModal]);

  if (!isFreePlan) {
    return (
      <SettingWrapper title="Plan">
        <SettingRow
          name={<span className={styles.proLabel}>Pro <span className={styles.proBadge}>Active</span></span>}
          desc="Unlimited transcription across all your devices"
        >
          <Button onClick={openBilling}>
            Manage Billing
          </Button>
        </SettingRow>
      </SettingWrapper>
    );
  }

  return (
    <SettingWrapper title="Plan">
      <div className={styles.upgradeCard}>
        <div className={styles.upgradeCardHeader}>
          <div>
            <div className={styles.upgradeCardTitle}>Get Pique Pro</div>
            <div className={styles.upgradeCardDesc}>
              Unlock the full power of Pique with unlimited transcription, synced seamlessly across all your devices.
            </div>
          </div>
          <span className={styles.upgradeCardBadge}>Free Plan</span>
        </div>
        <div className={styles.upgradeCardFeatures}>
          <div className={styles.upgradeCardFeature}>
            <span className={styles.upgradeCardFeatureCheck}>&#10003;</span>
            Unlimited transcription minutes
          </div>
          <div className={styles.upgradeCardFeature}>
            <span className={styles.upgradeCardFeatureCheck}>&#10003;</span>
            Sync notes across all devices
          </div>
          <div className={styles.upgradeCardFeature}>
            <span className={styles.upgradeCardFeatureCheck}>&#10003;</span>
            Priority AI processing
          </div>
        </div>
        <Button variant="primary" onClick={openPlans}>
          Upgrade to Pro
        </Button>
      </div>
    </SettingWrapper>
  );
};

const SignedInContent: FC = () => {
  const t = useI18n();
  const setSignOutModal = useSetAtom(openSignOutModalAtom);

  const onOpenSignOutModal = useCallback(() => {
    setSignOutModal(true);
  }, [setSignOutModal]);

  return (
    <>
      <SettingWrapper title="Account Information">
        <AvatarAndName />
      </SettingWrapper>

      <PlanSection />

      <SettingWrapper>
        <SettingRow
          name={t[`Sign out`]()}
          desc={t['com.pique.setting.sign.out.message']()}
          style={{ cursor: 'pointer' }}
          data-testid="sign-out-button"
          onClick={onOpenSignOutModal}
        >
          <ArrowRightSmallIcon />
        </SettingRow>
      </SettingWrapper>
    </>
  );
};

const SignedOutContent: FC = () => {
  const setAuthAtom = useSetAtom(authAtom);

  const openSignInModal = useCallback(() => {
    setAuthAtom(state => ({ ...state, openModal: true }));
  }, [setAuthAtom]);

  return (
    <SettingWrapper title="Sign In">
      <SettingRow
        name="Sign in to your account"
        desc="Sign in to sync your data across devices and access cloud features"
      >
        <Button variant="primary" onClick={openSignInModal}>
          Sign In
        </Button>
      </SettingRow>
    </SettingWrapper>
  );
};

const CONFIRM_PHRASE = 'delete all data';

const MyDataSection: FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [confirmInput, setConfirmInput] = useState('');
  const { workspacesService, authService } = useServices({
    WorkspacesService,
    AuthService,
  });
  const workspaces = useLiveData(workspacesService.list.workspaces$);
  const loginStatus = useLiveData(authService.session.status$);
  const isAuthenticated = loginStatus === 'authenticated';

  const allowDelete = confirmInput.toLowerCase() === CONFIRM_PHRASE;

  const handleClearAllData = useAsyncCallback(async () => {
    setIsResetting(true);
    try {
      // Step 1: Sign out if authenticated (clears auth tokens, vault, etc.)
      if (isAuthenticated) {
        try {
          await authService.signOut();
        } catch (e) {
          console.error('Failed to sign out:', e);
        }
      }

      // Step 2: Delete all workspaces through the service
      for (const workspace of workspaces) {
        try {
          await workspacesService.deleteWorkspace(workspace);
        } catch (e) {
          console.error('Failed to delete workspace:', workspace.id, e);
        }
      }

      // Step 3: Clear all IndexedDB databases
      if (window.indexedDB.databases) {
        const dbNames = await window.indexedDB.databases();
        for (const db of dbNames) {
          if (db.name) {
            try {
              window.indexedDB.deleteDatabase(db.name);
            } catch (e) {
              console.error('Failed to delete database:', db.name, e);
            }
          }
        }
      }

      // Step 4: Clear localStorage
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      // Step 5: Clear sessionStorage
      sessionStorage.clear();

      notify.success({
        title: 'All local data cleared',
        message: 'The app will now reload...',
      });

      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (e) {
      console.error('Failed to clear all data:', e);
      notify.error({
        title: 'Failed to clear data',
        message: String(e),
      });
      setIsResetting(false);
    }
  }, [workspaces, workspacesService, authService, isAuthenticated]);

  return (
    <>
      <SettingWrapper title="My Data">
        <div
          className={styles.collapsibleSectionHeader}
          onClick={() => setIsExpanded(!isExpanded)}
          data-testid="my-data-section-toggle"
        >
          <div className={styles.dangerSectionTitle}>
            <span>Reset Device</span>
          </div>
          <div className={styles.collapsibleSectionChevron}>
            {isExpanded ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronRight size={16} />
            )}
          </div>
        </div>

        {isExpanded && (
          <div className={styles.collapsibleSectionContent}>
            <p className={styles.dangerDescription}>
              Permanently delete all data stored on this device. This is
              irreversible.
            </p>
            <Button
              variant="error"
              onClick={() => setShowResetModal(true)}
              data-testid="clear-all-data-button"
            >
              Clear Data
            </Button>
          </div>
        )}
      </SettingWrapper>

      <ConfirmModal
        open={showResetModal}
        onOpenChange={(open: boolean) => {
          setShowResetModal(open);
          if (!open) setConfirmInput('');
        }}
        title="Reset Device?"
        cancelText="Cancel"
        confirmText={isResetting ? 'Clearing...' : 'Clear All Data'}
        confirmButtonOptions={{
          variant: 'error',
          disabled: !allowDelete || isResetting,
          ['data-testid' as string]: 'clear-all-data-confirm-button',
        }}
        onConfirm={handleClearAllData}
      >
        <p className={styles.modalDescription}>
          This will permanently delete all local data, preferences, and cached
          data from this device.
        </p>
        <p className={styles.modalDescription}>
          Cloud-synced data will remain available after signing back in.
        </p>
        <p className={styles.confirmInstruction}>
          Type <strong>{CONFIRM_PHRASE}</strong> to confirm:
        </p>
        <div className={styles.confirmInputWrapper}>
          <Input
            autoFocus
            value={confirmInput}
            onChange={setConfirmInput}
            placeholder={CONFIRM_PHRASE}
            size="large"
            data-testid="clear-all-data-confirm-input"
            onEnter={() => allowDelete && handleClearAllData()}
          />
        </div>
      </ConfirmModal>
    </>
  );
};

export const AccountSetting: FC = () => {
  const authService = useService(AuthService);
  const session = authService.session;
  const loginStatus = useLiveData(session.status$);
  const isAuthenticated = loginStatus === 'authenticated';

  useEffect(() => {
    session.revalidate();
  }, [session]);

  return (
    <>
      <SettingHeader
        title="Account"
        subtitle={
          isAuthenticated
            ? 'Manage your profile and preferences'
            : 'Sign in to access all features'
        }
        data-testid="account-title"
      />

      {isAuthenticated ? <SignedInContent /> : <SignedOutContent />}

      <MyDataSection />
    </>
  );
};
